'use client';

import type {FormEvent} from 'react';
import {createRef, type FC, useCallback, useState} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {CheckCircleIcon, ExclamationTriangleIcon} from '@heroicons/react/20/solid';
import clsx from 'clsx';
import type {FormPricesSchema} from 'schema/singletons/form-prices';

import type {OrderFormSchema} from 'components/content-blocks/order-form/schema';
import {Spinner} from 'components/spinner';

import {Checkbox} from './Checkbox';
import {orderFormFields} from './config';
import {FieldWrapper} from './field-wrapper';
import type {CakeSize} from './FormSchema';
import {Radio} from './Radio';

const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
const recaptchaRef = createRef<ReCAPTCHA>();

type Props = {
	section: OrderFormSchema;
	formPrices: FormPricesSchema;
};

/**
 * The character limits for the text on the cake.
 *
 * small -> Liten -> 0 characters
 * medium -> Medium -> 40 characters
 * large -> Stor -> 50 characters
 * xlarge -> Halvpanne -> 60 characters
 * xxlarge -> 3/4 Panne -> 80 characters
 * xxxlarge -> Helpanne -> 80 characters
 */
const characterLimitsOnCake = (size: CakeSize | string) => {
	switch (size) {
		case 'small':
			return 0;
		case 'medium':
			return 40;
		case 'large':
			return 50;
		case 'xlarge':
			return 60;
		case 'xxlarge':
			return 80;
		case 'xxxlarge':
			return 80;
		default:
			return 0;
	}
};

export const OrderFormRender: FC<Props> = ({section, formPrices}) => {
	const [submitError, setSubmitError] = useState<boolean>(false);
	const [isSubmitting, setSubmitting] = useState(false);
	const [isSubmitted, setSubmitted] = useState(false);
	const [formErrorText, setFormErrorText] = useState('');
	const [fileError, setFileError] = useState('');

	const [cakeSize, setCakeSize] = useState<CakeSize | string>('');
	const [cakeFill, setCakeFill] = useState<string[]>([]);
	const [cakeTop, setCakeTop] = useState('');
	const [occation, setOccation] = useState('');
	const [text, setText] = useState('');
	const [image, setImage] = useState<File | undefined>(undefined);
	const [otherCakes, setOtherCakes] = useState<string[]>([]);
	const [delivery, setDelivery] = useState('');
	const [contactForm, setContactForm] = useState({
		name: '',
		phone: '',
		email: '',
		address: '',
		deliveryDate: '',
	});
	const [bottom, setBottom] = useState('');

	const onContactFormChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const {name, value} = event.target;
			setContactForm((prev) => ({...prev, [name]: value}));
		},
		[],
	);

	const onCakeSizeChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setText('');
			setCakeSize(event.target.value);

			if (cakeSize === 'small') {
				setImage(undefined);
			}
		},
		[cakeSize],
	);

	const onCakeFillChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const {value} = event.target;
			if (cakeFill.includes(value)) {
				setCakeFill(cakeFill.filter((fill) => fill !== value));
			} else {
				setCakeFill([...cakeFill, value]);
			}
		},
		[cakeFill],
	);

	const onCakeTopChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setBottom('');
		setCakeTop(event.target.value);
	}, []);

	const onOccationChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setOccation(event.target.value);
	}, []);

	const onTextChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setText(event.target.value);
	}, []);

	const onImageChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];

		// max size 5MB
		if (file && file.size > 5 * 1024 * 1024) {
			setFileError('Bildet er for stort. Maks 5MB.');
		} else {
			setFileError('');
			setFormErrorText('');
		}

		setImage(file);
	}, []);

	const onOtherCakesChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const {value} = event.target;
			if (otherCakes.includes(value)) {
				setOtherCakes(otherCakes.filter((cake) => cake !== value));
			} else {
				setOtherCakes([...otherCakes, value]);
			}
		},
		[otherCakes],
	);

	const onDeliveryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setDelivery(event.target.value);
	}, []);

	const onBottomChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setBottom(event.target.value);
	}, []);

	const isValidForm = useCallback(() => {
		if (cakeSize !== 'small' && text.length > characterLimitsOnCake(cakeSize)) {
			setFormErrorText('Tekst på kaken er for lang!');
			return false;
		}

		if (!delivery) {
			setFormErrorText('Utleveringssted må velges!');
			return false;
		}

		// max size 5MB
		if (image && image.size > 5 * 1024 * 1024) {
			setFormErrorText('Bildet er for stort. Maks 5MB.');
			return false;
		}

		return true;
	}, [cakeSize, text, delivery, image]);

	const onSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			if (!isValidForm()) {
				return;
			}

			try {
				event.preventDefault();
				setSubmitting(true);

				const recaptchaToken = await recaptchaRef.current?.executeAsync();

				const body = new FormData(event.target as HTMLFormElement);
				body.append('ccDelivery', section.variant === 'cc' ? 'true' : 'false');

				const response = await fetch('/api/order', {
					method: 'POST',
					body: body,
					headers: {
						'g-recaptcha-token': recaptchaToken ?? '',
					},
				});

				const data = await response.json();

				if (!response.ok) {
					setSubmitting(false);
					setFormErrorText(data.message);
					return;
				}

				setSubmitted(true);
			} catch (error) {
				console.error(error);
				setSubmitError(true);
				setSubmitting(false);
			}
		},
		[isValidForm, section.variant],
	);

	const handleReset = () => {
		setCakeSize('');
		setCakeFill([]);
		setCakeTop('');
		setOccation('');
		setText('');
		setImage(undefined);
		setOtherCakes([]);
		setDelivery('');
		setContactForm({
			name: '',
			phone: '',
			email: '',
			address: '',
			deliveryDate: '',
		});
		setSubmitError(false);
		setSubmitted(false);
		setBottom('');
		setFormErrorText('');
		setSubmitting(false);
	};

	if (isSubmitted) {
		return (
			<>
				<form
					className={clsx('flex', 'justify-center')}
					onSubmit={(event) => event.preventDefault()}
					onReset={handleReset}
				>
					<div
						className={clsx(
							'max-w-7xl',
							'w-full',
							'py-8',
							'px-8',
							'flex',
							'flex-col',
							'gap-12',
							'items-center',
						)}
					>
						<div className={clsx('rounded-md', 'bg-green-50', 'p-4')}>
							<div className={clsx('flex')}>
								<div className={clsx('shrink-0')}>
									<CheckCircleIcon
										className={clsx('h-7', 'w-7', 'text-green-400')}
										aria-hidden="true"
									/>
								</div>
								<div className={clsx('ml-3')}>
									<p className={clsx('font-medium', 'text-green-800')}>
										Din bestilling er sendt!
									</p>
									<p className={clsx('mt-2', 'text-green-700')}>
										Du vil motta en bekreftelse på mail om at bestillingen er
										mottatt.
										<br />
										Er du usikker kontakt bakeriet på tlf:{' '}
										<a
											className={clsx(
												'underline',
												'hover:no-underline',
												'font-semibold',
											)}
											href="tel:61143900"
										>
											61 14 39 00
										</a>{' '}
										eller mail:{' '}
										<a
											className={clsx(
												'underline',
												'hover:no-underline',
												'font-semibold',
											)}
											href="mailto:ordre@kjelstad.as"
										>
											ordre@kjelstad.as
										</a>
										<br />
										<br />
										Bestillingen kan hentes på ditt valgte utleveringssted, på
										valgt leveringsdag innenfor ordinær åpningstid.
										<br />
										Din bestilling betales ved henting i butikk/utsalg.
									</p>
								</div>
							</div>
						</div>
						<div>
							<ResetButton />
						</div>
					</div>
				</form>
			</>
		);
	}

	return (
		<form className={clsx('flex', 'justify-center')} onSubmit={onSubmit} onReset={handleReset}>
			<div
				className={clsx(
					'max-w-7xl',
					'w-full',
					'py-8',
					'px-8',
					'flex',
					'flex-col',
					'gap-12',
					isSubmitting && 'opacity-50',
					isSubmitting && 'pointer-events-none',
				)}
			>
				<div className={clsx('grid', 'grid-cols-1', 'md:grid-cols-2', 'gap-12')}>
					{/**
					 * Cake size selection
					 */}
					<FieldWrapper variant="column" legend={orderFormFields.cake.legend}>
						{orderFormFields.cake.options.map((cake) => {
							const price = formPrices[cake.value];
							return (
								<Radio
									key={cake.value}
									name={orderFormFields.cake.name}
									value={cake.value}
									label={cake.label}
									description={cake.description}
									price={price}
									checked={cakeSize === cake.value}
									onChange={onCakeSizeChange}
								/>
							);
						})}
					</FieldWrapper>

					<div className={clsx('flex', 'flex-col', 'gap-12')}>
						{/**
						 * Cake fill selection
						 */}
						<FieldWrapper variant="row" legend={orderFormFields.fill.legend}>
							{orderFormFields.fill.options.map((fill) => {
								return (
									<Checkbox
										key={fill.value}
										name={orderFormFields.fill.name}
										value={fill.value}
										label={fill.label}
										onChange={onCakeFillChange}
										checked={cakeFill.includes(fill.value)}
									/>
								);
							})}
						</FieldWrapper>

						{/**
						 * Cake top selection
						 */}
						<FieldWrapper variant="row" legend={orderFormFields.top.legend}>
							{orderFormFields.top.options.map((top) => {
								return (
									<Radio
										key={top.value}
										name={orderFormFields.top.name}
										value={top.value}
										label={top.label}
										checked={cakeTop === top.value}
										onChange={onCakeTopChange}
										hideInput
									/>
								);
							})}
						</FieldWrapper>

						{/**
						 * Cake bottom selection
						 */}
						{cakeTop === 'chocolate' && (
							<FieldWrapper variant="row" legend={orderFormFields.bottom.legend}>
								<Radio
									hideInput
									name="bottom"
									value="sugarbread"
									label="Sukkerbrød"
									checked={bottom === 'sugarbread'}
									onChange={onBottomChange}
								/>
								<Radio
									hideInput
									name="bottom"
									value="chocolate"
									label="Sjokoladebunn"
									checked={bottom === 'chocolate'}
									onChange={onBottomChange}
								/>
							</FieldWrapper>
						)}
					</div>
				</div>

				{/**
				 * Customization of cake
				 */}
				<div className={clsx('grid', 'grid-cols-1', 'md:grid-cols-3', 'gap-12')}>
					{/* Occation */}
					<fieldset className={clsx('flex', 'flex-col')}>
						<legend className={clsx('text-2xl', 'mb-6')}>Anledning</legend>

						<label className={clsx('text-base', 'flex-1')}>
							<span className={clsx('sr-only')}>Anledning</span>
							<textarea
								name="occasion"
								value={occation}
								onChange={onOccationChange}
								className={clsx(
									'block',
									'w-full',
									'h-full',
									'rounded-md',
									'py-2',
									'text-gray-900',
									'shadow-sm',
									'border',
									'border-gray-300',
									'focus-within:border-gray-300',
									'focus-within:ring-2',
									'focus-within:ring-offset-2',
									'focus-within:ring-primary-dark',
									'placeholder:text-gray-400',
									'sm:text-sm',
									'sm:leading-6',
								)}
							/>
						</label>
					</fieldset>

					{/* Text on cake */}
					{/* The length of character is based on the size of the cake */}
					{cakeSize === 'small' ? null : (
						<>
							{' '}
							<fieldset className={clsx('flex', 'flex-col')}>
								<legend className={clsx('text-2xl', 'mb-6')}>
									Tekst{' '}
									<span
										className={clsx(
											'text-gray-900',
											'font-medium',
											'text-base',
										)}
									>
										(For bestillingskaker)
									</span>
								</legend>

								<div
									className={clsx(
										'text-sm',
										'font-normal',
										'text-gray-500',
										'mb-6',
									)}
								>
									Tekst kan kun inneholde bokstaver og vanlige tegn. Emoji o.l vil
									ikke komme med.
								</div>

								<label className={clsx('text-base', 'flex-1')}>
									<span className={clsx('sr-only')}>Tekst</span>
									<textarea
										name="text"
										value={text}
										onChange={onTextChange}
										maxLength={characterLimitsOnCake(cakeSize)}
										disabled={cakeSize === 'small'}
										className={clsx(
											'block',
											'w-full',
											'h-full',
											'rounded-md',
											'py-2',
											'text-gray-900',
											'shadow-sm',
											'border',
											'border-gray-300',
											'focus-within:border-gray-300',
											'focus-within:ring-2',
											'focus-within:ring-offset-2',
											'focus-within:ring-primary-dark',
											'placeholder:text-gray-400',
											'sm:text-sm',
											'sm:leading-6',
										)}
									/>
								</label>
								<span
									className={clsx(
										'text-sm',
										'font-normal',
										'text-gray-500',
										'mt-1',
									)}
								>
									{cakeSize === 'small' ? (
										'Ikke mulig med tekst på liten kake'
									) : (
										<>
											{text.length} av {characterLimitsOnCake(cakeSize)} tegn
										</>
									)}
								</span>
							</fieldset>
						</>
					)}

					{/* Image on cake */}
					{cakeSize === 'small' ? null : (
						<fieldset>
							<legend className={clsx('text-2xl', 'mb-6')}>
								Bilde{' '}
								<span className={clsx('text-gray-900', 'font-medium', 'text-base')}>
									(For bestillingskaker): kr. {formPrices.image},-
								</span>
							</legend>

							<div
								className={clsx('text-sm', 'font-normal', 'text-gray-500', 'mb-6')}
							>
								Bildet (JPG-format) kan vedlegges denne bestilling ved å klikke på
								knappen under og laste opp billedfil fra din pc (maks 5 MB).
								Alternativt kan det ettersendes til ordre@kjelstad.as. Husk og
								skrive navn, leveringssted og dato dersom bildet ettersendes på
								e-post.
							</div>

							<label
								className={clsx('flex', 'flex-col', 'flex-1', 'gap-2', 'text-base')}
							>
								<span
									className={clsx(
										'text-base',
										'font-medium',
										'leading-6',
										'text-gray-900',
										'sr-only',
									)}
								>
									Bilde
								</span>
								<input
									type="file"
									name="image"
									accept="image/*"
									onChange={onImageChange}
									className={clsx(
										'block',
										'w-full',
										'text-sm',
										'p-1',
										'text-gray-500',
										'rounded-md',
										'focus-within:outline-0',
										'focus-within:border-gray-300',
										'focus-within:ring-2',
										'focus-within:ring-offset-2',
										'focus-within:ring-primary-dark',
										'file:border',
										'file:bg-white',
										'file:ring-0',
										'file:px-6',
										'file:py-4',
										'file:mr-6',
										'file:items-center',
										'file:cursor-pointer',
										'file:rounded-lg',
										'file:border-gray-200',
										'file:border-solid',
										'file:shadow-sm',
									)}
								/>
							</label>
							{fileError && (
								<p className={clsx('text-sm', 'font-medium', 'text-red-500')}>
									{fileError}
								</p>
							)}
						</fieldset>
					)}
				</div>

				{/**
				 * Other cakes to order
				 */}
				<FieldWrapper variant="row" legend={orderFormFields.otherCakes.legend}>
					{orderFormFields.otherCakes.options.map((cake) => {
						const price = formPrices[cake.value];
						return (
							<Checkbox
								key={cake.value}
								name={orderFormFields.otherCakes.name}
								value={cake.value}
								label={cake.label}
								description={cake.description}
								price={price}
								onChange={onOtherCakesChange}
								checked={otherCakes.includes(cake.value)}
							/>
						);
					})}
				</FieldWrapper>

				{/**
				 * Delivery selection
				 */}
				{section.variant === 'cake' && (
					<FieldWrapper variant="row" legend={orderFormFields.delivery.legend} required>
						{orderFormFields.delivery.options.map((deliveryOption) => {
							return (
								<Radio
									key={deliveryOption.value}
									name={orderFormFields.delivery.name}
									value={deliveryOption.value}
									label={`${deliveryOption.label} ${deliveryOption.description}`}
									description={deliveryOption.openingHours.join('\n')}
									checked={delivery === deliveryOption.value}
									onChange={onDeliveryChange}
								/>
							);
						})}
					</FieldWrapper>
				)}

				{/**
				 * Delivery selection for CC
				 */}
				{section.variant === 'cc' && (
					<FieldWrapper variant="row" legend={orderFormFields.deliveryCC.legend} required>
						{orderFormFields.deliveryCC.options.map((deliveryOption) => {
							return (
								<Radio
									key={deliveryOption.value}
									name={orderFormFields.deliveryCC.name}
									value={deliveryOption.value}
									label={deliveryOption.label}
									description={deliveryOption.description}
									checked={delivery === deliveryOption.value}
									onChange={onDeliveryChange}
								/>
							);
						})}
					</FieldWrapper>
				)}

				<div className={clsx('flex', 'flex-col', 'items-center', 'gap-12')}>
					{/**
					 * Customer contact information
					 */}
					<fieldset className={clsx('md:max-w-xl', 'w-full')}>
						<legend className={clsx('text-2xl', 'mb-6')}>Kontaktinformasjon</legend>

						<div className={clsx('grid', 'grid-cols-1', 'gap-4', 'md:gap-8')}>
							<label
								className={clsx('flex', 'flex-col', 'flex-1', 'gap-2', 'text-base')}
							>
								<span
									className={clsx(
										'after:content-["*"]',
										'after:ml-0.5',
										'after:text-red-500',
										'text-base',
										'font-medium',
										'leading-6',
										'text-gray-900',
									)}
								>
									Fornavn og etternavn
								</span>
								<input
									type="text"
									name="name"
									className={clsx(
										'block',
										'w-full',
										'rounded-md',
										'py-2',
										'text-gray-900',
										'shadow-sm',
										'border',
										'border-gray-300',
										'focus-within:border-gray-300',
										'focus-within:ring-2',
										'focus-within:ring-offset-2',
										'focus-within:ring-primary-dark',
										'placeholder:text-gray-400',
									)}
									value={contactForm.name}
									onChange={onContactFormChange}
									required
								/>
							</label>

							<label
								className={clsx('flex', 'flex-col', 'flex-1', 'gap-2', 'text-base')}
							>
								<span
									className={clsx(
										'after:content-["*"]',
										'after:ml-0.5',
										'after:text-red-500',
										'text-base',
										'font-medium',
										'leading-6',
										'text-gray-900',
									)}
								>
									Telefonnummer
								</span>
								<input
									type="tel"
									name="phone"
									className={clsx(
										'block',
										'w-full',
										'rounded-md',
										'py-2',
										'text-gray-900',
										'shadow-sm',
										'border',
										'border-gray-300',
										'focus-within:border-gray-300',
										'focus-within:ring-2',
										'focus-within:ring-offset-2',
										'focus-within:ring-primary-dark',
										'placeholder:text-gray-400',
									)}
									value={contactForm.phone}
									onChange={onContactFormChange}
									required
								/>
							</label>

							<label
								className={clsx('flex', 'flex-col', 'flex-1', 'gap-2', 'text-base')}
							>
								<span
									className={clsx(
										'after:content-["*"]',
										'after:ml-0.5',
										'after:text-red-500',
										'text-base',
										'font-medium',
										'leading-6',
										'text-gray-900',
									)}
								>
									E-post
								</span>
								<input
									type="email"
									name="email"
									className={clsx(
										'block',
										'w-full',
										'rounded-md',
										'py-2',
										'text-gray-900',
										'shadow-sm',
										'border',
										'border-gray-300',
										'focus-within:border-gray-300',
										'focus-within:ring-2',
										'focus-within:ring-offset-2',
										'focus-within:ring-primary-dark',
										'placeholder:text-gray-400',
									)}
									value={contactForm.email}
									onChange={onContactFormChange}
									required
								/>
							</label>

							<label
								className={clsx('flex', 'flex-col', 'flex-1', 'gap-2', 'text-base')}
							>
								<span
									className={clsx(
										'text-base',
										'font-medium',
										'leading-6',
										'text-gray-900',
									)}
								>
									Adresse
								</span>
								<textarea
									name="address"
									className={clsx(
										'block',
										'w-full',
										'rounded-md',
										'py-2',
										'text-gray-900',
										'shadow-sm',
										'border',
										'border-gray-300',
										'focus-within:border-gray-300',
										'focus-within:ring-2',
										'focus-within:ring-offset-2',
										'focus-within:ring-primary-dark',
										'placeholder:text-gray-400',
									)}
									rows={4}
									value={contactForm.address}
									onChange={onContactFormChange}
								/>
							</label>

							<label
								className={clsx('flex', 'flex-col', 'flex-1', 'gap-2', 'text-base')}
							>
								<span
									className={clsx(
										'after:content-["*"]',
										'after:ml-0.5',
										'after:text-red-500',
										'text-base',
										'font-medium',
										'leading-6',
										'text-gray-900',
									)}
								>
									Leveringsdato
								</span>
								<input
									type="date"
									name="deliveryDate"
									className={clsx(
										'block',
										'w-full',
										'rounded-md',
										'py-2',
										'text-gray-900',
										'shadow-sm',
										'border',
										'border-gray-300',
										'placeholder:text-gray-400',
										'focus-within:border-gray-300',
										'focus-within:ring-2',
										'focus-within:ring-offset-2',
										'focus-within:ring-primary-dark',
									)}
									value={contactForm.deliveryDate}
									onChange={onContactFormChange}
									min={new Date().toISOString().split('T')[0]}
									required
								/>
							</label>
						</div>
					</fieldset>

					<Warning />
				</div>

				{formErrorText && <Error message={formErrorText} />}
				{submitError && <Error message="Noe gikk galt. Prøv igjen senere." />}

				<Buttons submitting={isSubmitting} />

				{siteKey && <ReCAPTCHA ref={recaptchaRef} sitekey={siteKey} size="invisible" />}
			</div>
		</form>
	);
};

const Warning = () => {
	return (
		<div className={clsx('md:max-w-xl', 'w-full', 'rounded-md', 'bg-yellow-50', 'p-4')}>
			<div className={clsx('flex')}>
				<div className={clsx('shrink-0')}>
					<ExclamationTriangleIcon
						className={clsx('h-5', 'w-5', 'text-yellow-400')}
						aria-hidden="true"
					/>
				</div>
				<div className={clsx('ml-3')}>
					<p className={clsx('text-sm', 'font-medium', 'text-yellow-800')}>NB!</p>
					<p className={clsx('mt-2', 'text-sm', 'text-yellow-700')}>
						Husk å sjekke bestillingsfrister! <br />
						Du skal få en bekreftelse på mail om at bestillingen er mottatt.
						<br />
						Er du usikker kontakt bakeriet på tlf:{' '}
						<a
							className={clsx('underline', 'hover:no-underline', 'font-semibold')}
							href="tel:61143900"
						>
							61 14 39 00
						</a>{' '}
						eller mail:{' '}
						<a
							className={clsx('underline', 'hover:no-underline', 'font-semibold')}
							href="mailto:ordre@kjelstad.as"
						>
							ordre@kjelstad.as
						</a>
					</p>
				</div>
			</div>
		</div>
	);
};

const Error = ({message}: {message: string}) => {
	return (
		<div
			aria-live="polite"
			className={clsx(
				'md:max-w-xl',
				'w-full',
				'mx-auto',
				'rounded-md',
				'bg-red-50',
				'p-4',
				'text-red-700',
				'font-medium',
				'text-center',
			)}
		>
			{message}
		</div>
	);
};

const Buttons = ({submitting}: {submitting: boolean}) => {
	return (
		<div className={clsx('flex', 'md:gap-12', 'gap-6', 'justify-center', 'mt-8')}>
			<ResetButton />
			<button
				className={clsx(
					'flex',
					'items-center',
					'cursor-pointer',
					'rounded-lg',
					'border',
					'bg-primary-dark',
					'text-white',
					'px-6',
					'py-4',
					'gap-6',
					'shadow-sm',
					'focus:outline-none',
					'group/field',
					'active:ring-2',
					'active:ring-offset-2',
					'active:ring-primary-dark',
					'focus-within:ring-2',
					'focus-within:ring-offset-2',
					'focus-within:ring-primary-dark',
					'font-medium',
				)}
				type="submit"
			>
				Send bestilling
				{submitting && <Spinner color="light" size="medium" />}
			</button>
		</div>
	);
};

const ResetButton = () => {
	return (
		<button
			className={clsx(
				'flex',
				'items-center',
				'cursor-pointer',
				'rounded-lg',
				'border',
				'bg-white',
				'px-6',
				'py-4',
				'gap-6',
				'shadow-sm',
				'focus:outline-none',
				'group/field',
				'active:ring-2',
				'active:ring-offset-2',
				'active:ring-primary-dark',
				'focus-within:ring-2',
				'focus-within:ring-offset-2',
				'focus-within:ring-primary-dark',
				'font-medium',
				'text-gray-900',
			)}
			type="reset"
		>
			Tilbakestill
		</button>
	);
};
